import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { CreditCard, Gift } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

function ThankYou({ config, onClose }) {
  const pc = config.primaryColor;
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
      <div style={{ background:'#fff', borderRadius:20, maxWidth:400, width:'100%', padding:36, textAlign:'center' }}>
        <div style={{ fontSize:56, marginBottom:16 }}>🎁</div>
        <h2 style={{ fontWeight:800, fontSize:22, color:'#111827', marginBottom:8 }}>¡Tarjeta enviada!</h2>
        <p style={{ color:'#6b7280', marginBottom:24, fontSize:14 }}>El destinatario recibirá su tarjeta de regalo muy pronto.</p>
        <button onClick={onClose} style={{ padding:'12px 32px', background:pc, color:'#fff', border:'none', borderRadius:12, fontWeight:700, cursor:'pointer', fontSize:14 }}>Cerrar</button>
      </div>
    </div>
  );
}

export default function GiftCardWidgetPublicPage() {
  const { restaurantId } = useParams();
  const [searchParams] = useSearchParams();

  const rawAmounts = searchParams.get('amounts') || '50,100,200,500';
  const presetAmounts = rawAmounts.split(',').map(Number).filter(Boolean);

  const config = {
    primaryColor: `#${searchParams.get('color') || 'F97316'}`,
    backgroundColor: `#${searchParams.get('bg') || 'FFFFFF'}`,
    textColor: `#${searchParams.get('text') || '1E293B'}`,
    theme: searchParams.get('theme') || 'light',
    fontFamily: searchParams.get('font') || 'inter',
    borderRadius: parseInt(searchParams.get('radius') || '16', 10),
    currency: searchParams.get('currency') || 'MXN',
    currencySymbol: searchParams.get('sym') || '$',
    presetAmounts,
    allowCustomAmount: searchParams.get('allowCustom') !== '0',
    minCustomAmount: parseInt(searchParams.get('minAmt') || '50', 10),
    maxCustomAmount: parseInt(searchParams.get('maxAmt') || '5000', 10),
    validityDays: parseInt(searchParams.get('validity') || '365', 10),
    discountOnline: parseInt(searchParams.get('discount') || '0', 10),
    deliveryMethods: (searchParams.get('delivery') || 'email').split(',').filter(Boolean),
    paymentMethods: (searchParams.get('payments') || 'stripe,paypal,restaurant').split(',').filter(Boolean),
    buttonText: searchParams.get('btnText') ? decodeURIComponent(searchParams.get('btnText')) : 'Comprar tarjeta',
    buttonStyle: searchParams.get('btnStyle') || 'filled',
    cardTitle: searchParams.get('title') ? decodeURIComponent(searchParams.get('title')) : 'Tarjetas de Regalo',
    cardSubtitle: searchParams.get('subtitle') ? decodeURIComponent(searchParams.get('subtitle')) : 'El regalo perfecto para momentos especiales',
  };

  const [step, setStep] = useState(1);
  const [selectedAmount, setSelectedAmount] = useState(presetAmounts[1] || presetAmounts[0] || 100);
  const [isCustom, setIsCustom] = useState(false);
  const [customAmount, setCustomAmount] = useState('');
  const [delivery, setDelivery] = useState(config.deliveryMethods[0] || 'email');
  const [paymentMethod, setPaymentMethod] = useState(config.paymentMethods[0] || 'stripe');
  const [form, setForm] = useState({ senderName:'', recipientName:'', recipientEmail:'', recipientPhone:'', message:'' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const pc = config.primaryColor;
  const isDark = config.theme === 'dark';
  const br = `${config.borderRadius}px`;
  const ff = { inter:"'Inter',sans-serif", poppins:"'Poppins',sans-serif", playfair:"'Playfair Display',serif" }[config.fontFamily] || "'Inter',sans-serif";
  const sym = config.currencySymbol;
  const amt = isCustom ? parseFloat(customAmount) || 0 : selectedAmount;
  const discount = config.discountOnline;
  const finalAmt = discount > 0 && paymentMethod !== 'restaurant' ? (amt * (1 - discount / 100)).toFixed(2) : amt;

  const btnStyle = {
    backgroundColor: config.buttonStyle === 'filled' ? pc : 'transparent',
    color: config.buttonStyle === 'filled' ? '#fff' : pc,
    border: config.buttonStyle === 'ghost' ? 'none' : `2px solid ${pc}`,
    borderRadius: `calc(${br} - 4px)`,
    padding: '14px 24px', fontWeight: 700, fontSize: 15, cursor: 'pointer', width: '100%', fontFamily: ff,
  };

  const handleSubmit = async () => {
    if (!form.senderName || !form.recipientName) return;
    setSubmitting(true);
    try {
      await fetch(`${API_URL}/api/gift-cards/public/${restaurantId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, amount: finalAmt, currency: config.currency, delivery, paymentMethod, restaurantId }),
      });
    } catch { }
    finally { setSubmitting(false); setDone(true); }
  };

  const cardBorder = (active) => `2px solid ${active ? pc : (isDark ? '#475569' : '#e2e8f0')}`;
  const cardBg = (active) => active ? `${pc}12` : (isDark ? '#1e293b' : '#fff');

  return (
    <div style={{ minHeight:'100vh', background: isDark?'#0f172a':'#f8fafc', display:'flex', alignItems:'center', justifyContent:'center', padding:16, fontFamily:ff }}>
      <div style={{ width:'100%', maxWidth:460, backgroundColor:config.backgroundColor, borderRadius:br, boxShadow:'0 8px 40px rgba(0,0,0,0.12)', overflow:'hidden' }}>
        {/* Header */}
        <div style={{ background:`linear-gradient(135deg,${pc},${pc}cc)`, padding:'28px 24px', textAlign:'center' }}>
          <div style={{ fontSize:40, marginBottom:8 }}>🎁</div>
          <h1 style={{ color:'#fff', fontWeight:800, fontSize:20, margin:'0 0 6px' }}>{config.cardTitle}</h1>
          <p style={{ color:'rgba(255,255,255,0.8)', fontSize:13, margin:0 }}>{config.cardSubtitle}</p>
        </div>

        {/* Step indicator */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:'16px 24px', background:isDark?'#1e293b':'#f8fafc', gap:8 }}>
          {[1,2,3].map((s,i) => (
            <div key={s} style={{ display:'flex', alignItems:'center', gap:8 }}>
              <div style={{ width:28, height:28, borderRadius:'50%', background:step>=s?pc:'#e5e7eb', color:step>=s?'#fff':'#9ca3af', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:700, transition:'background 0.2s' }}>{s}</div>
              {i < 2 && <div style={{ width:32, height:2, background:step>s?pc:'#e5e7eb' }} />}
            </div>
          ))}
        </div>

        <div style={{ padding:'20px 24px 28px', color:config.textColor }}>
          {/* Step 1: Amount & Delivery */}
          {step === 1 && (
            <>
              <h3 style={{ fontWeight:700, fontSize:16, margin:'0 0 14px' }}>Elige el monto</h3>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:16 }}>
                {presetAmounts.map(a => (
                  <button key={a} onClick={()=>{setSelectedAmount(a);setIsCustom(false);}}
                    style={{ padding:'16px 12px', textAlign:'center', cursor:'pointer', border:cardBorder(!isCustom&&selectedAmount===a), background:cardBg(!isCustom&&selectedAmount===a), borderRadius:12, transition:'all 0.15s' }}>
                    <div style={{ fontSize:20, fontWeight:800, color:!isCustom&&selectedAmount===a?pc:config.textColor }}>{sym}{a.toLocaleString()}</div>
                    <div style={{ fontSize:11, color:`${config.textColor}66`, marginTop:2 }}>{config.currency}</div>
                  </button>
                ))}
                {config.allowCustomAmount && (
                  <button onClick={()=>setIsCustom(true)}
                    style={{ padding:'14px 12px', textAlign:'center', cursor:'pointer', border:cardBorder(isCustom), background:cardBg(isCustom), borderRadius:12, gridColumn:'span 2' }}>
                    <div style={{ fontSize:14, fontWeight:700, color:isCustom?pc:config.textColor }}>Monto personalizado</div>
                    {isCustom && (
                      <input type="number" value={customAmount} onChange={e=>setCustomAmount(e.target.value)} placeholder={`${sym}${config.minCustomAmount} – ${sym}${config.maxCustomAmount}`} min={config.minCustomAmount} max={config.maxCustomAmount}
                        style={{ marginTop:8, padding:'8px 12px', border:`1.5px solid ${pc}`, borderRadius:8, fontSize:14, textAlign:'center', width:'60%', outline:'none', background:'transparent', color:config.textColor }} onClick={e=>e.stopPropagation()} />
                    )}
                  </button>
                )}
              </div>

              {config.deliveryMethods.length > 1 && (
                <>
                  <h3 style={{ fontWeight:700, fontSize:15, margin:'0 0 10px' }}>Forma de entrega</h3>
                  <div style={{ display:'flex', gap:8, marginBottom:16 }}>
                    {config.deliveryMethods.map(m=>(
                      <button key={m} onClick={()=>setDelivery(m)}
                        style={{ flex:1, padding:'12px', border:cardBorder(delivery===m), background:cardBg(delivery===m), borderRadius:10, cursor:'pointer', fontSize:13, fontWeight:600, color:delivery===m?pc:config.textColor }}>
                        {m==='email'?'📧 Email':m==='whatsapp'?'💬 WhatsApp':'🖨️ Impresa'}
                      </button>
                    ))}
                  </div>
                </>
              )}

              <div style={{ background:`${pc}10`, borderRadius:12, padding:'14px 18px', marginBottom:20, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <span style={{ fontSize:14 }}>Total</span>
                <span style={{ fontSize:24, fontWeight:900, color:pc }}>{sym}{amt.toLocaleString()} {config.currency}</span>
              </div>
              <button style={btnStyle} onClick={()=>amt>0&&setStep(2)}>Continuar →</button>
            </>
          )}

          {/* Step 2: Recipient info */}
          {step === 2 && (
            <>
              <h3 style={{ fontWeight:700, fontSize:16, margin:'0 0 14px' }}>Datos del destinatario</h3>
              <div style={{ display:'flex', flexDirection:'column', gap:12, marginBottom:20 }}>
                {[
                  {key:'senderName', label:'Tu nombre (quien regala) *', ph:'Tu nombre completo'},
                  {key:'recipientName', label:'Nombre del destinatario *', ph:'A quién va dirigida'},
                  delivery==='email' ? {key:'recipientEmail', label:'Email del destinatario *', ph:'email@ejemplo.com', type:'email'} : null,
                  delivery==='whatsapp' ? {key:'recipientPhone', label:'WhatsApp del destinatario *', ph:'+52 55 0000 0000', type:'tel'} : null,
                ].filter(Boolean).map(f=>(
                  <div key={f.key}>
                    <label style={{ fontSize:12, fontWeight:600, color:`${config.textColor}aa`, display:'block', marginBottom:4 }}>{f.label}</label>
                    <input type={f.type||'text'} placeholder={f.ph} value={form[f.key]} onChange={e=>setForm(p=>({...p,[f.key]:e.target.value}))}
                      style={{ width:'100%', padding:'11px 13px', border:`1.5px solid #e2e8f0`, borderRadius:10, fontSize:13, outline:'none', boxSizing:'border-box', fontFamily:ff, color:config.textColor }} />
                  </div>
                ))}
                <div>
                  <label style={{ fontSize:12, fontWeight:600, color:`${config.textColor}aa`, display:'block', marginBottom:4 }}>Mensaje personalizado</label>
                  <textarea value={form.message} onChange={e=>setForm(p=>({...p,message:e.target.value}))} placeholder="¡Disfruta esta experiencia única!" rows={3}
                    style={{ width:'100%', padding:'11px 13px', border:'1.5px solid #e2e8f0', borderRadius:10, fontSize:13, outline:'none', resize:'none', boxSizing:'border-box', fontFamily:ff, color:config.textColor }} />
                </div>
              </div>
              <div style={{ display:'flex', gap:10 }}>
                <button onClick={()=>setStep(1)} style={{ flex:1, padding:'12px', border:'1.5px solid #e5e7eb', borderRadius:10, background:'transparent', cursor:'pointer', fontWeight:600, fontSize:14, color:config.textColor }}>← Volver</button>
                <button onClick={()=>(form.senderName&&form.recipientName)&&setStep(3)} style={{ ...btnStyle, flex:2 }}>Continuar →</button>
              </div>
            </>
          )}

          {/* Step 3: Payment */}
          {step === 3 && (
            <>
              <h3 style={{ fontWeight:700, fontSize:16, margin:'0 0 14px' }}>Método de pago</h3>
              <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:20 }}>
                {config.paymentMethods.includes('stripe') && (
                  <label style={{ display:'flex', alignItems:'center', gap:14, padding:'14px 16px', border:cardBorder(paymentMethod==='stripe'), background:cardBg(paymentMethod==='stripe'), borderRadius:12, cursor:'pointer' }}>
                    <input type="radio" name="pay" checked={paymentMethod==='stripe'} onChange={()=>setPaymentMethod('stripe')} style={{ accentColor:pc }} />
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:700, fontSize:14 }}>💳 Tarjeta de crédito / débito</div>
                      <div style={{ fontSize:11, color:'#9ca3af' }}>Visa, Mastercard, AMEX · Pago seguro</div>
                    </div>
                    {discount>0 && <span style={{ fontSize:12, fontWeight:700, color:'#22c55e', background:'#f0fdf4', padding:'2px 8px', borderRadius:20 }}>−{discount}%</span>}
                  </label>
                )}
                {config.paymentMethods.includes('paypal') && (
                  <label style={{ display:'flex', alignItems:'center', gap:14, padding:'14px 16px', border:cardBorder(paymentMethod==='paypal'), background:cardBg(paymentMethod==='paypal'), borderRadius:12, cursor:'pointer' }}>
                    <input type="radio" name="pay" checked={paymentMethod==='paypal'} onChange={()=>setPaymentMethod('paypal')} style={{ accentColor:pc }} />
                    <div style={{ flex:1 }}>
                      <div style={{ fontWeight:700, fontSize:14 }}><span style={{ color:'#003087' }}>Pay</span><span style={{ color:'#009cde' }}>Pal</span></div>
                      <div style={{ fontSize:11, color:'#9ca3af' }}>Paga con tu cuenta PayPal</div>
                    </div>
                    {discount>0 && <span style={{ fontSize:12, fontWeight:700, color:'#22c55e', background:'#f0fdf4', padding:'2px 8px', borderRadius:20 }}>−{discount}%</span>}
                  </label>
                )}
                {config.paymentMethods.includes('restaurant') && (
                  <label style={{ display:'flex', alignItems:'center', gap:14, padding:'14px 16px', border:cardBorder(paymentMethod==='restaurant'), background:cardBg(paymentMethod==='restaurant'), borderRadius:12, cursor:'pointer' }}>
                    <input type="radio" name="pay" checked={paymentMethod==='restaurant'} onChange={()=>setPaymentMethod('restaurant')} style={{ accentColor:pc }} />
                    <div>
                      <div style={{ fontWeight:700, fontSize:14 }}>🏪 Pagar en el restaurante</div>
                      <div style={{ fontSize:11, color:'#9ca3af' }}>Te enviaremos la tarjeta tras confirmar el pago</div>
                    </div>
                  </label>
                )}
              </div>

              <div style={{ background:`${pc}10`, borderRadius:12, padding:'14px 18px', marginBottom:20, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div>
                  <div style={{ fontSize:13, fontWeight:600 }}>Total a pagar</div>
                  {discount>0&&paymentMethod!=='restaurant' && <div style={{ fontSize:11, color:'#6b7280' }}>Con {discount}% de descuento</div>}
                </div>
                <div style={{ textAlign:'right' }}>
                  {discount>0&&paymentMethod!=='restaurant' && <div style={{ fontSize:12, textDecoration:'line-through', color:'#9ca3af' }}>{sym}{amt.toLocaleString()}</div>}
                  <div style={{ fontSize:24, fontWeight:900, color:pc }}>{sym}{parseFloat(finalAmt).toLocaleString()}</div>
                </div>
              </div>

              <div style={{ display:'flex', gap:10 }}>
                <button onClick={()=>setStep(2)} style={{ flex:1, padding:'12px', border:'1.5px solid #e5e7eb', borderRadius:10, background:'transparent', cursor:'pointer', fontWeight:600, fontSize:14, color:config.textColor }}>← Volver</button>
                <button onClick={handleSubmit} disabled={submitting}
                  style={{ ...btnStyle, flex:2, opacity:submitting?0.7:1 }}>
                  {submitting ? 'Procesando...' : `${paymentMethod==='restaurant'?'Solicitar':'Pagar'} ${sym}${parseFloat(finalAmt).toLocaleString()}`}
                </button>
              </div>
            </>
          )}
        </div>

        <p style={{ textAlign:'center', fontSize:11, color:'#9ca3af', paddingBottom:16, margin:0 }}>Powered by <strong style={{ color:'#f97316' }}>OkReserva</strong></p>
      </div>
      {done && <ThankYou config={config} onClose={()=>setDone(false)} />}
    </div>
  );
}
